import { z } from 'zod';

const IdSchema = z.string().min(1).max(160);
const RefSchema = z.string().min(1).max(240);

export const SdkModuleCodeSchema = z.string().regex(
  /^[A-Z][A-Z0-9]{1,7}$/,
  'SDK module code must be 2-8 uppercase alphanumeric characters and start with a letter.',
);
export type SdkModuleCode = z.infer<typeof SdkModuleCodeSchema>;

export const SdkModuleKindSchema = z.enum(['core-top-layer', 'horizontal', 'vertical', 'solution']);
export type SdkModuleKind = z.infer<typeof SdkModuleKindSchema>;

export const SdkCompositionTypeSchema = z.enum(['top-layer', 'module', 'menu-set', 'runtime-pack']);
export type SdkCompositionType = z.infer<typeof SdkCompositionTypeSchema>;

export const SdkRuntimeSurfaceSchema = z.enum(['web', 'pwa', 'share', 'self-service', 'admin', 'ai']);
export type SdkRuntimeSurface = z.infer<typeof SdkRuntimeSurfaceSchema>;

export const SdkReleaseChannelSchema = z.enum(['sandbox', 'beta', 'stable', 'latest']);
export type SdkReleaseChannel = z.infer<typeof SdkReleaseChannelSchema>;

export const SdkNavigationItemSchema = z.object({
  itemId: IdSchema,
  label: z.string().min(1).max(160),
  route: z.string().min(1).max(300),
  slotRef: RefSchema,
  icon: z.string().min(1).max(80).optional(),
  order: z.number().int().nonnegative(),
  parentItemId: IdSchema.optional(),
  surfaces: z.array(SdkRuntimeSurfaceSchema).min(1),
  requiredCapabilityRefs: z.array(RefSchema).default([]),
  requiredPermissionRefs: z.array(RefSchema).default([]),
});
export type SdkNavigationItem = z.infer<typeof SdkNavigationItemSchema>;

export const SdkNavigationPolicySchema = z.object({
  sourceOfTruth: z.enum(['module-blueprint', 'module-profile', 'boox-network-registry']),
  routeNamespace: z.string().regex(/^\/[a-z0-9-]+$/, 'routeNamespace must be a stable root route such as /ta.'),
  forbidHardcodedFrontendRoutes: z.boolean().default(true),
  requireStableRouteIds: z.boolean().default(true),
  fallbackPolicy: z.enum(['last-known-good', 'bundled-baseline', 'blocked']).default('last-known-good'),
  items: z.array(SdkNavigationItemSchema).min(1),
}).superRefine((policy, ctx) => {
  const seenRoutes = new Set<string>();
  const seenOrders = new Set<number>();
  for (const [index, item] of policy.items.entries()) {
    if (!item.route.startsWith(policy.routeNamespace)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items', index, 'route'],
        message: `route must stay under routeNamespace ${policy.routeNamespace}`,
      });
    }
    if (seenRoutes.has(item.route)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items', index, 'route'],
        message: `duplicate navigation route ${item.route}`,
      });
    }
    seenRoutes.add(item.route);
    if (seenOrders.has(item.order)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items', index, 'order'],
        message: `duplicate navigation order ${item.order}`,
      });
    }
    seenOrders.add(item.order);
  }
});
export type SdkNavigationPolicy = z.infer<typeof SdkNavigationPolicySchema>;

export const SdkDataClassSchema = z.enum(['core', 'transactional', 'extension', 'reporting', 'audit']);
export type SdkDataClass = z.infer<typeof SdkDataClassSchema>;

export const SdkDataAccessModeSchema = z.enum(['runtime-query', 'action-executor', 'sdk-table-crud', 'report-query']);
export type SdkDataAccessMode = z.infer<typeof SdkDataAccessModeSchema>;

export const SdkDataOperationSchema = z.enum(['read', 'create', 'update', 'delete', 'archive', 'export']);
export type SdkDataOperation = z.infer<typeof SdkDataOperationSchema>;

export const SdkDataBindingSchema = z.object({
  bindingId: IdSchema,
  label: z.string().min(1).max(160),
  dataClass: SdkDataClassSchema,
  accessMode: SdkDataAccessModeSchema,
  resourceRef: RefSchema,
  tableName: z.string().min(1).max(160).optional(),
  queryRefs: z.array(RefSchema).default([]),
  actionRefs: z.array(RefSchema).default([]),
  reportRefs: z.array(RefSchema).default([]),
  allowedOperations: z.array(SdkDataOperationSchema).min(1),
  roleRefs: z.array(RefSchema).min(1),
  userAssignmentRequired: z.boolean().default(false),
  directDatabaseAccess: z.literal(false).default(false),
  tenantScoped: z.boolean().default(true),
  auditRequired: z.boolean().default(true),
  approvalRequiredForBulkMutation: z.boolean().default(true),
}).superRefine((binding, ctx) => {
  if (binding.accessMode === 'sdk-table-crud' && binding.dataClass === 'core') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['accessMode'],
      message: 'sdk-table-crud is forbidden for core data; use runtime-query/action-executor contracts instead.',
    });
  }
  if (binding.accessMode === 'sdk-table-crud' && !binding.tableName) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tableName'],
      message: 'sdk-table-crud requires an explicit tableName.',
    });
  }
  if (binding.accessMode === 'runtime-query' && binding.queryRefs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['queryRefs'],
      message: 'runtime-query requires at least one queryRef.',
    });
  }
  if (binding.accessMode === 'action-executor' && binding.actionRefs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['actionRefs'],
      message: 'action-executor requires at least one actionRef.',
    });
  }
  if (binding.accessMode === 'report-query' && binding.reportRefs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reportRefs'],
      message: 'report-query requires at least one reportRef.',
    });
  }
});
export type SdkDataBinding = z.infer<typeof SdkDataBindingSchema>;

export const SdkUserAssignmentPolicySchema = z.object({
  assignmentPolicyId: IdSchema,
  assignablePrincipalTypes: z.array(z.enum(['user', 'team', 'role', 'external-party'])).min(1),
  requiredAssignmentFields: z.array(RefSchema).default([]),
  defaultOwnerResolver: z.enum(['current-user', 'role-default', 'tenant-default', 'manual']).default('manual'),
  allowUnassignedWork: z.boolean().default(false),
  notifyOnAssignment: z.boolean().default(true),
});
export type SdkUserAssignmentPolicy = z.infer<typeof SdkUserAssignmentPolicySchema>;

export const SdkSandboxPolicySchema = z.object({
  sandboxRequired: z.boolean().default(true),
  syntheticDataRequired: z.boolean().default(true),
  outboundSideEffects: z.enum(['blocked', 'approval-only', 'allowed']).default('blocked'),
  promotionRequiresEvidence: z.boolean().default(true),
});
export type SdkSandboxPolicy = z.infer<typeof SdkSandboxPolicySchema>;

export const SdkModuleTestCaseSchema = z.object({
  caseId: IdSchema,
  label: z.string().min(1).max(160),
  kind: z.enum(['navigation', 'data-access', 'permission', 'workflow', 'report', 'sandbox']),
  expectedEvidence: z.array(RefSchema).min(1),
});
export type SdkModuleTestCase = z.infer<typeof SdkModuleTestCaseSchema>;

export const SdkWorkflowStageSchema = z.enum([
  'receiving',
  'qc',
  'putaway',
  'yard-gate',
  'yard-move',
  'yard-staging',
  'picking',
  'packing',
  'shipping',
  'stock-count',
]);
export type SdkWorkflowStage = z.infer<typeof SdkWorkflowStageSchema>;

export const SdkWorkflowStepSchema = z.object({
  stepId: IdSchema,
  label: z.string().min(1).max(160),
  stage: SdkWorkflowStageSchema,
  sequence: z.number().int().positive(),
  queryRefs: z.array(RefSchema).default([]),
  actionRefs: z.array(RefSchema).default([]),
  requiredEvidenceRefs: z.array(RefSchema).default([]),
  lotControlRequired: z.boolean().default(false),
  terminal: z.boolean().default(false),
});
export type SdkWorkflowStep = z.infer<typeof SdkWorkflowStepSchema>;

export const SdkWorkflowTemplateSchema = z.object({
  workflowId: IdSchema,
  label: z.string().min(1).max(160),
  entityRef: RefSchema,
  initialStepId: IdSchema,
  steps: z.array(SdkWorkflowStepSchema).min(1),
}).superRefine((workflow, ctx) => {
  const stepIds = new Set<string>();
  for (const [index, step] of workflow.steps.entries()) {
    if (stepIds.has(step.stepId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', index, 'stepId'],
        message: `duplicate workflow stepId ${step.stepId}`,
      });
    }
    stepIds.add(step.stepId);
  }
  if (!stepIds.has(workflow.initialStepId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['initialStepId'],
      message: 'initialStepId must match one workflow step.',
    });
  }
});
export type SdkWorkflowTemplate = z.infer<typeof SdkWorkflowTemplateSchema>;

export const SdkLotControlPolicySchema = z.object({
  policyId: IdSchema,
  lotNoSource: z.enum(['system-generated', 'customer-provided', 'carrier-document', 'mixed']),
  lotNoRequiredFromStage: SdkWorkflowStageSchema,
  uniquenessScope: z.enum(['tenant', 'warehouse', 'customer', 'job', 'container']),
  splitLotAllowed: z.boolean().default(true),
  mergeLotAllowed: z.boolean().default(false),
  traceabilityRefs: z.array(RefSchema).min(1),
});
export type SdkLotControlPolicy = z.infer<typeof SdkLotControlPolicySchema>;

export const SdkOperationalControlPolicySchema = z.object({
  controlId: IdSchema,
  label: z.string().min(1).max(160),
  appendOnlyActivityLog: z.boolean().default(true),
  requireAuditForStatusChange: z.boolean().default(true),
  lotControl: SdkLotControlPolicySchema.optional(),
});
export type SdkOperationalControlPolicy = z.infer<typeof SdkOperationalControlPolicySchema>;

export const SdkDeploymentManifestSchema = z.object({
  registrySource: z.enum(['boox-network', 'bundled-dev', 'local-sandbox']),
  pinnedVersion: z.string().min(1).max(80),
  channel: SdkReleaseChannelSchema,
  marketOverlays: z.array(z.enum(['global', 'sg', 'my', 'id'])).default(['global']),
  failover: z.object({
    strategy: z.enum(['last-known-good', 'blocked']),
    allowLatestFallback: z.literal(false).default(false),
  }),
});
export type SdkDeploymentManifest = z.infer<typeof SdkDeploymentManifestSchema>;

export const SdkModuleBlueprintSchema = z.object({
  blueprintId: IdSchema,
  moduleCode: SdkModuleCodeSchema,
  moduleId: z.string().regex(
    /^[a-z][a-z0-9]{1,7}$/,
    'moduleId must be 2-8 lowercase alphanumeric characters and start with a letter.',
  ),
  moduleName: z.string().min(1).max(160),
  version: z.string().min(1).max(80),
  kind: SdkModuleKindSchema,
  compositionType: SdkCompositionTypeSchema.default('module'),
  description: z.string().max(1000).optional(),
  dependencies: z.array(RefSchema).default([]),
  navigation: SdkNavigationPolicySchema,
  dataAccess: z.array(SdkDataBindingSchema).min(1),
  workflowTemplates: z.array(SdkWorkflowTemplateSchema).default([]),
  controlPolicies: z.array(SdkOperationalControlPolicySchema).default([]),
  assignmentPolicy: SdkUserAssignmentPolicySchema.optional(),
  sandboxPolicy: SdkSandboxPolicySchema.default({}),
  tests: z.array(SdkModuleTestCaseSchema).min(1),
  deployment: SdkDeploymentManifestSchema,
}).superRefine((blueprint, ctx) => {
  if (blueprint.moduleCode.toLowerCase() !== blueprint.moduleId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['moduleId'],
      message: 'moduleId must match moduleCode in lowercase.',
    });
  }
  const namespace = `/${blueprint.moduleId}`;
  if (blueprint.navigation.routeNamespace !== namespace) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['navigation', 'routeNamespace'],
      message: `routeNamespace must be ${namespace} for module ${blueprint.moduleCode}.`,
    });
  }
});
export type SdkModuleBlueprint = z.infer<typeof SdkModuleBlueprintSchema>;

export function parseSdkModuleBlueprint(input: unknown): SdkModuleBlueprint {
  return SdkModuleBlueprintSchema.parse(input);
}
